import cn from "classnames";
import { Button } from "components/Button";
import { isEmpty, isEqual, isNil } from "lodash";
import { Stack } from "react-bootstrap";
import { DataGridFilterInput } from "./DataGridFilterInput";
import { DataGridColumnConfig, DataGridFilterType } from "./types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useMediaQuery from "components/utils/useMediaQuery";
import { DataGridFilterModal } from "./DataGridFilterModal";
import { DataGridSortModal } from "./DataGridSortModal";
import { useFilter } from "./useFilter";
import { useCallback, useState } from "react";

export const DataGridFilter: React.FC<{
    columns: DataGridColumnConfig[];
    filter?: DataGridFilterType;
    sortBy?: string;
    sortAsc?: boolean;
    filterSelected?: string;
    isFilterOpen: boolean;
    isSortOpen?: boolean;
    isFilterEmpty: boolean;
    toggleFilter: () => void;
    toggleSort: () => void;
    getFilterValue: ReturnType<typeof useFilter>["getFilterValue"];
    setFilterInputRef: ReturnType<typeof useFilter>["setFilterInputRef"];
    canSelectRows?: boolean;
    onFilterChange: (searchAttr: string) => void;
    onFilterValueChange: (key: string, value: string, submitFilter?: boolean) => void;
    onFilterValueKeyDown: (key: string, event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onFieldClean: (key: string) => void;
    onFilterSubmit: (filterValue?: DataGridFilterType) => void;
    onFilterClear: () => void;
    onSortClear: () => void;
    onFilterInputFocus: (key: string) => void;
    onSortChange: (key: string) => void;
}> = ({
    columns,
    filter: filterProp = {},
    sortBy,
    sortAsc,
    filterSelected,
    isFilterOpen,
    isSortOpen,
    isFilterEmpty,
    toggleFilter,
    toggleSort,
    getFilterValue,
    setFilterInputRef,
    canSelectRows,
    onFilterChange,
    onFilterValueKeyDown,
    onFieldClean,
    onFilterSubmit,
    onFilterClear,
    onSortClear,
    onFilterInputFocus,
    onSortChange,
}) => {
    const [filter, setFilter] = useState(filterProp);
    const [filterUnsubmit, setFilterUnsubmit] = useState(true);
    const [previousFilter, setPreviousFilter] = useState(filterProp);
    const isMobile = useMediaQuery("(max-width: 768px)");

    if (!isEqual(previousFilter, filterProp)) {
        setFilter(filterProp);
        setFilterUnsubmit(true);
        setPreviousFilter(filterProp);
    }

    const onChange = useCallback(
        (value: string, key?: string) => {
            if (isNil(key)) return;

            if (value.trim().length === 0) {
                const { [key]: _, ...newFilter } = filter;
                setFilter(newFilter);
                setFilterUnsubmit(isEqual(newFilter, previousFilter));
                return;
            }

            const newFilter = { ...filter, [key]: value };
            setFilter({ ...filter, [key]: value });
            setFilterUnsubmit(isEqual(newFilter, previousFilter));
        },
        [filter, previousFilter]
    );

    const onApply = () => {
        onFilterSubmit(filter);
        setFilterUnsubmit(true);
    };

    return (
        <>
            {filterSelected && isMobile && isFilterOpen && (
                <DataGridFilterModal
                    isFilterOpen={isFilterOpen}
                    isFilterEmpty={isFilterEmpty}
                    filter={filter}
                    columns={columns}
                    toggleFilter={toggleFilter}
                    toggleSort={toggleSort}
                    filterSelected={filterSelected}
                    getFilterValue={getFilterValue}
                    onFilterValueKeyDown={onFilterValueKeyDown}
                    onFilterSubmit={onFilterSubmit}
                    onFilterChange={onFilterChange}
                    onFieldClean={onFieldClean}
                    onFilterInputFocus={onFilterInputFocus}
                />
            )}
            {isMobile && isSortOpen && !isNil(sortBy) && !isNil(sortAsc) && (
                <DataGridSortModal
                    isSortOpen={isSortOpen}
                    isFilterEmpty={isFilterEmpty}
                    filter={filter}
                    sortBy={sortBy}
                    sortAsc={sortAsc}
                    columns={columns}
                    toggleFilter={toggleFilter}
                    toggleSort={toggleSort}
                    getFilterValue={getFilterValue}
                    onFilterClear={onFilterClear}
                    onSortClear={onSortClear}
                    onFieldClean={onFieldClean}
                    onFilterInputFocus={onFilterInputFocus}
                    onSortChange={onSortChange}
                />
            )}
            {!isMobile && isFilterOpen && (
                <tr className="data-grid-filter d-none d-lg-table-row">
                    {!isMobile && canSelectRows && (
                        <td className="col-auto d-flex d-lg-table-cell py-3 select-column align-middle border border-top-0 border-start-0"></td>
                    )}
                    {columns.map((column, index) => (
                        <td
                            key={index}
                            className={cn(column.className, "align-middle fs-7 border border-top-0 border-end-0", {
                                "border-start-0": index === 0,
                                "col-sorted": !isEmpty(column.sort),
                            })}
                        >
                            <DataGridFilterInput
                                ref={(e) => setFilterInputRef?.(column.key, e)}
                                column={column}
                                value={getFilterValue(column.key)}
                                showPlaceholder={true}
                                onChange={onChange}
                                onKeyDown={onFilterValueKeyDown}
                            />
                        </td>
                    ))}
                    <td
                        style={{ width: "7rem" }}
                        className={cn("actions-filter fs-7 position-sticky top-0 end-0 border border-top-0 border-end-0")}
                    >
                        <Stack className="d-flex justify-content-center" direction="horizontal" gap={0}>
                            {filterUnsubmit && (
                                <Button
                                    className="filter-toggler d-flex align-items-center mx-auto"
                                    variant="link"
                                    title="Filter"
                                    aria-label="Filter"
                                    onClick={() => toggleFilter()}
                                >
                                    <FontAwesomeIcon className="pe-2 fs-6" icon={["fal", "magnifying-glass"]} />
                                    <FontAwesomeIcon className="ps-1 fs-5" icon={["fal", "angle-up"]} />
                                </Button>
                            )}
                            {!filterUnsubmit && (
                                <Button className="py-1" variant="primary" title="Filter" aria-label="Filter" onClick={onApply}>
                                    Apply
                                </Button>
                            )}
                        </Stack>
                    </td>
                </tr>
            )}
        </>
    );
};
